import {
  InfiniteData,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { produce } from 'immer';
import { useSnackbar } from 'ui-components';
import { mediaKitsApi } from '~/api/mediaKits';
import {
  SNACKBAR_ERROR_GENERIC_OPTIONS,
  SNACKBAR_GENERIC_OPTIONS,
} from '~/constants';
import { logger } from '~/core/logger/logger';
import { MEDIA_KIT } from '~/queries/keys';
import { MEDIA_KITS_DD_SECTION } from '~/screens/PageBuilder';

import { MediaKitListResp } from './list';

type Error = {
  code: number;
  message: string;
};

export const useDeleteMediaKit = () => {
  const qc = useQueryClient();
  const snackbar = useSnackbar();

  return useMutation({
    mutationFn: ({ id }: { id: string }) =>
      mediaKitsApi.delete(`custom-shareable/${id}`).then((r) => r.data as null),

    onMutate: async ({ id }) => {
      await qc.cancelQueries([MEDIA_KIT]);
      qc.setQueryData<InfiniteData<MediaKitListResp>>(
        [MEDIA_KIT],
        produce((draft) => {
          if (!draft) return draft;

          draft.pages = draft.pages.map((page) => ({
            ...page,
            results: page.results.filter((mediaKit) => mediaKit.id !== id),
          }));
        }),
      );
    },

    onSuccess: () => {
      snackbar.success({
        ...SNACKBAR_GENERIC_OPTIONS,
        description: 'Media Kit successfully removed',
      });
    },

    onError: (error: AxiosError<Error>) => {
      const errorMessage =
        error.response?.data?.message || error.message || 'Unknown Error';
      snackbar.error({
        ...SNACKBAR_ERROR_GENERIC_OPTIONS,
        description: `Error deleting media kit: ${errorMessage}`,
      });
      logger
        .withMetadata({
          error,
          section: MEDIA_KITS_DD_SECTION,
        })
        .error('Media Kit deletion Error');
    },

    onSettled: () => {
      qc.invalidateQueries([MEDIA_KIT]);
    },
  });
};
